import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Activity, AlertTriangle, CheckCircle, TrendingUp } from 'lucide-react';

const MonitoringDashboard = ({ platforms }) => {
  // Generate mock time-series data for ingestion rates
  const generateTimeSeriesData = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    return hours?.map(hour => ({
      hour: `${hour}:00`,
      facebook: Math.floor(Math.random() * 150) + 50,
      linkedin: Math.floor(Math.random() * 100) + 30,
      reddit: Math.floor(Math.random() * 250) + 100,
      instagram: Math.floor(Math.random() * 80) + 20,
      twitter: hour < 20 ? Math.floor(Math.random() * 200) + 50 : 0
    }));
  };

  // Generate platform performance data
  const platformPerformanceData = platforms?.map(p => ({
    name: p?.name,
    apiUsage: p?.apiUsage,
    dataRate: parseInt(p?.dataFlowRate) || 0,
    health: p?.health === 'healthy' ? 100 : p?.health === 'warning' ? 60 : 20
  }));

  const timeSeriesData = generateTimeSeriesData();

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getHealthIcon = (health) => {
    switch (health) {
      case 'healthy': return <CheckCircle className="w-5 h-5" />;
      case 'warning': return <AlertTriangle className="w-5 h-5" />;
      case 'error': return <AlertTriangle className="w-5 h-5" />;
      default: return <Activity className="w-5 h-5" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Unified Monitoring Dashboard</h2>
        <p className="text-sm text-gray-600">
          Real-time performance metrics and data collection analytics
        </p>
      </div>
      {/* Data Ingestion Rate Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900">Data Ingestion Rate (Last 24 Hours)</h3>
            <p className="text-sm text-gray-600">Real-time data collection throughput by platform</p>
          </div>
          <TrendingUp className="w-5 h-5 text-blue-600" />
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={timeSeriesData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="hour" 
              tick={{ fontSize: 12 }}
              interval={2}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="facebook" 
              stroke="#1877F2" 
              strokeWidth={2}
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="linkedin" 
              stroke="#0A66C2" 
              strokeWidth={2}
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="reddit" 
              stroke="#FF4500" 
              strokeWidth={2}
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="instagram" 
              stroke="#E4405F" 
              strokeWidth={2}
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="twitter" 
              stroke="#1DA1F2" 
              strokeWidth={2}
              dot={false}
              strokeDasharray="5 5"
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            <span className="font-medium">Note:</span> Twitter/X data collection stopped at 20:00 due to authentication error
          </p>
        </div>
      </div>
      {/* Platform Performance Chart */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900">Platform Performance Overview</h3>
            <p className="text-sm text-gray-600">API usage, data rates, and health metrics</p>
          </div>
          <Activity className="w-5 h-5 text-blue-600" />
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={platformPerformanceData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="apiUsage" fill="#3B82F6" name="API Usage %" />
            <Bar dataKey="health" fill="#10B981" name="Health Score" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Platform Health Matrix */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Platform Health Matrix</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {platforms?.map((platform) => (
            <div 
              key={platform?.id}
              className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{platform?.icon}</span>
                  <span className="font-medium text-gray-900">{platform?.name}</span>
                </div>
                <div className={getHealthColor(platform?.health)}>
                  {getHealthIcon(platform?.health)}
                </div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className="font-medium capitalize">{platform?.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">API Usage:</span>
                  <span className="font-medium">{platform?.apiUsage}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Data Rate:</span>
                  <span className="font-medium">{platform?.dataFlowRate}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Latency:</span>
                  <span className="font-medium">
                    {Math.floor(Math.random() * 500) + 100}ms
                  </span>
                </div>
              </div>

              {(platform?.error || platform?.warning) && (
                <div className={`mt-3 p-2 rounded text-xs ${
                  platform?.error ? 'bg-red-50 text-red-800' : 'bg-yellow-50 text-yellow-800'
                }`}>
                  {platform?.error || platform?.warning}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Error Logs Summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Recent Alerts & Errors</h3>
        <div className="space-y-3">
          {platforms?.filter(p => p?.error || p?.warning)?.map((platform, index) => (
              <div 
                key={index}
                className={`p-4 rounded-lg border ${
                  platform?.error 
                    ? 'bg-red-50 border-red-200' :'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                    platform?.error ? 'text-red-600' : 'text-yellow-600'
                  }`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-gray-900">
                        {platform?.name}
                      </span>
                      <span className="text-sm text-gray-600">
                        {new Date(platform.lastSync || Date.now())?.toLocaleTimeString()}
                      </span>
                    </div>
                    <p className={`text-sm ${
                      platform?.error ? 'text-red-800' : 'text-yellow-800'
                    }`}>
                      {platform?.error || platform?.warning}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          
          {platforms?.filter(p => p?.error || p?.warning)?.length === 0 && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <p className="text-sm text-green-800">
                  All platforms operating normally - no errors or warnings detected
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MonitoringDashboard;